import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { isMobile, isMobileOnly } from 'mobile-device-detect'
import { View, Views } from 'react-big-calendar'

import moment from 'moment'

import { CalendarEventEditModal } from 'components/Calendar/Meetings/CalendarEventEditModal'
import { CalanderWrapper } from 'components/Calendar/style'
import { getReturnToPath, getRoleFromPath, navigateTo } from 'utils/helper'
import { CalenderSettingContainer } from 'pages/Owner/container'
import { isEmpty } from 'lodash'
import { CalendarViewer } from 'components/Calendar/Meetings/CalendarViewer'
import { CalendarSkeletonLoader } from 'components/Calendar/Meetings/CalendarSkeletonLoader'
import { useLocation } from 'react-router-dom'
import useGetStartEndDate from 'hooks/useGetStartEndDate'
import { CALENDAR } from 'config'
import {
  GetUserCalendarEventsQueryVariables,
  UserProfile,
} from '../../../__generated__/api-types-and-hooks'
import { ReduxAction } from 'store/types'
import { ICalendarEvent } from 'types'
import history from 'utils/history'

// Update the 'ko' locale definition without leaving it active globally.
// moment.updateLocale(name, config) switches the active locale as a side
// effect, which silently breaks any consumer (or test) that imports this
// module and then calls moment().format(...) expecting the default locale.
const _prevMomentLocale = moment.locale()
moment.updateLocale('ko', {
  week: {
    dow: 0,
    doy: 1,
  },
})
moment.locale(_prevMomentLocale)

const checkMobile = () => {
  return window.innerWidth < 500 || isMobileOnly
}

interface IMeetingsProps {
  user: UserProfile
  currentCalendarDate: Date
  errorMessage: string
  refetch: boolean
  isDisabled: boolean
  isMeetingDeletion: boolean
  cancelMeetingLoading: boolean
  isMeetingModalOpen: boolean
  showInternalMeetingsOnly: boolean
  calenderEventsLoading: boolean
  events: ICalendarEvent[]
  stepType
  userMeeting
  advisorMeetingRequestButtonEnabled?: boolean
  setCurrentCalendarDateAction: ReduxAction<Date>
  getUserCalendarEventsAction: ReduxAction<GetUserCalendarEventsQueryVariables>
  getMyToolsAction: ReduxAction<void>
  totalUpComingMeetingsAction
  cancelUserCalendarEventAction
  resetUserCalendarEventsAction
  updateUserCalendarEventAction
  getUserMeetingByEventIdAction
  openMeetingConfirmationModalBoxAction
}

const Meetings = ({
  user,
  events,
  refetch,
  stepType,
  isDisabled,
  errorMessage,
  userMeeting,
  isMeetingDeletion,
  cancelMeetingLoading,
  isMeetingModalOpen,
  advisorMeetingRequestButtonEnabled,
  cancelUserCalendarEventAction,
  getUserCalendarEventsAction,
  totalUpComingMeetingsAction,
  resetUserCalendarEventsAction,
  updateUserCalendarEventAction,
  getMyToolsAction,
  showInternalMeetingsOnly,
  getUserMeetingByEventIdAction,
  calenderEventsLoading,
  openMeetingConfirmationModalBoxAction,
  currentCalendarDate,
  setCurrentCalendarDateAction,
}: IMeetingsProps) => {
  const { t, i18n } = useTranslation()
  useEffect(() => {
    moment.locale(i18n.language || 'en')
  }, [i18n.language])
  const [view, setView] = useState<View>(checkMobile() ? Views.DAY : Views.MONTH)
  const [calendarEvents, setCalendarEvents] = useState<ICalendarEvent[]>([])
  // Show the calendar skeleton until the first fetch completes. Without this,
  // the very first render happens with calenderEventsLoading=false (Redux
  // initial state) and an empty events array, flashing an empty calendar
  // grid before the useEffect dispatches the fetch — that flash is what
  // looks like a brief "white screen" on slow networks.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const wasLoadingRef = useRef(false)
  useEffect(() => {
    if (wasLoadingRef.current && !calenderEventsLoading) {
      setHasLoadedOnce(true)
    }
    wasLoadingRef.current = calenderEventsLoading
  }, [calenderEventsLoading])
  const [openModal, setOpenModal] = useState(false)
  const [selectedEvent, setSelectedEvent]: any = useState()
  // "Show cancelled meetings" toggle — default ON, persisted in localStorage.
  const [showCancelledMeetings, setShowCancelledMeetings] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true
    const stored = window.localStorage.getItem('calendar.showCancelledMeetings')
    return stored === null ? true : stored !== 'false'
  })
  const onToggleCancelled = () => {
    const next = !showCancelledMeetings
    setShowCancelledMeetings(next)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('calendar.showCancelledMeetings', String(next))
    }
  }
  const modalHandler = () => {
    setOpenModal(!openModal)
    // Reset to read-only mode when modal closes
    if (openModal) {
      setShowEditButton(true)
    }
  }
  const [timeMin, setTimeMin] = useState(moment().toISOString())
  const [timeMax, setTimeMax] = useState(moment().toISOString())
  const [showEditButton, setShowEditButton] = useState(true)
  const location = useLocation()

  const userRole = getRoleFromPath()
  useEffect(() => {
    totalUpComingMeetingsAction(5)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { start_date, end_date } = useGetStartEndDate(currentCalendarDate)

  useEffect(() => {
    setTimeMin(start_date)
    setTimeMax(end_date)

    if (refetch || !isMeetingModalOpen) {
      let payload = {
        calendarName: showInternalMeetingsOnly ? CALENDAR.INTERNAL : '',
        timeMin: moment(start_date).format('YYYY-MM-DDTHH:mm:ss-00:00'),
        timeMax: moment(end_date).format('YYYY-MM-DDTHH:mm:ss-00:00'),
      }
      getUserCalendarEventsAction(payload)
      getMyToolsAction()
    }

    return () => resetUserCalendarEventsAction()

    // `view` is intentionally omitted: start/end dates derive from `currentCalendarDate`
    // only (see useGetStartEndDate — it uses a fixed MONTH range), so switching views
    // should not trigger a redundant refetch (#6620).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCalendarDate, refetch, isMeetingModalOpen, showInternalMeetingsOnly])

  useEffect(() => {
    const filtered = showCancelledMeetings
      ? events
      : events.filter((e: any) => e?.status !== 'cancelled')
    setCalendarEvents(filtered)
  }, [events, showCancelledMeetings])

  useEffect(() => {
    const param = getReturnToPath()
    if (param.eventId && !isEmpty(calendarEvents)) {
      const event = calendarEvents.find((event) => event.eventId === param.eventId)
      if (event) {
        setSelectedEvent(event)
        setOpenModal(true)
      }
      history.replace(`/${userRole}/${user.tenantId}/calendar`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarEvents])

  useEffect(() => {
    const splitPath = window.location.href.split('details=')
    const eventId = splitPath[splitPath.length - 1]
    const shouldFetchSpecificMeeting =
      eventId &&
      (isEmpty(userMeeting?.id) || userMeeting?.id !== eventId) &&
      window.location.href.includes('details')

    if (shouldFetchSpecificMeeting) {
      getUserMeetingByEventIdAction({ id: eventId })
    }

    const shouldShowMeetingDetailsModal =
      !isEmpty(userMeeting?.id) &&
      window.location.href.includes('details') &&
      userMeeting?.id === eventId

    if (shouldShowMeetingDetailsModal) {
      setSelectedEvent(userMeeting)
      setOpenModal(true)
      navigateTo(userRole, 'calendar')
    }
    // eslint-disable-next-line
  }, [userMeeting?.title, location.key])

  const onSelectEvent = useCallback(
    (calEvent) => {
      setOpenModal(!openModal)
      setSelectedEvent(calEvent)
    },
    [openModal]
  )
  return (
    <>
      {openModal === true ? (
        <CalendarEventEditModal
          user={user}
          closeModal={modalHandler}
          cancelUserCalendarEventAction={cancelUserCalendarEventAction}
          timeMin={timeMin}
          timeMax={timeMax}
          event={selectedEvent}
          showEditButton={showEditButton}
          setShowEditButton={setShowEditButton}
          updateMeetingAction={updateUserCalendarEventAction}
          openMeetingConfirmationModalBoxAction={openMeetingConfirmationModalBoxAction}
          stepType={stepType}
          isMeetingDeletion={isMeetingDeletion}
          errorMessage={errorMessage}
          isDisabled={isDisabled || cancelMeetingLoading}
        />
      ) : null}

      {/* Calendar */}
      <div
        id="calendar"
        className="xs:col-span-12 lg:col-span-10 mt-8 lg:block"
        data-testid="calendar-panel"
      >
        <div className="flex items-center justify-end mb-3">
          <label className="flex items-center gap-2 text-sm text-primary-text cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showCancelledMeetings}
              onChange={onToggleCancelled}
              className="h-4 w-4 cursor-pointer accent-primary"
              data-testid="show-cancelled-toggle"
            />
            {t('calendar.showCancelledMeetings')}
          </label>
        </div>
        {calenderEventsLoading || !hasLoadedOnce ? (
          <CalendarSkeletonLoader />
        ) : (
          <CalanderWrapper>
            <CalendarViewer
              calendarEvents={calendarEvents}
              view={view}
              date={currentCalendarDate}
              onSelectEvent={onSelectEvent}
              isMobile={isMobile}
              setView={setView}
              setDate={setCurrentCalendarDateAction}
            />
          </CalanderWrapper>
        )}
      </div>

      {/* calendar details & settings */}
      <CalenderSettingContainer
        advisorMeetingRequestButtonEnabled={advisorMeetingRequestButtonEnabled}
      />
    </>
  )
}

export default Meetings
